import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { PageHeader } from '@/components';
import { brand } from '@/theme/theme';
import { tenantApi } from '@/api/tenant';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useAuthStore } from '@/stores/authStore';
import { getConfig, updateConfig, type SubscriptionConfig } from '@/api/subscription';
import { adminApi, adminSettingsApi, type AdminTenant } from '@/api/admin';
import type { ApiError, TenantSettings } from '@/types';

const DEFAULT_SERVER_SETTINGS: TenantSettings = {
  showPriceToParties: true,
  showPriceToSalesReps: true,
  showStockToParties: true,
  showStockToSalesReps: true,
  showSchemesToParties: true,
  showSchemesToSalesReps: true,
  showHeldProductsToSalesReps: false,
  minOrderAmount: 0,
  orderPauseFrom: null,
  orderPauseTo: null,
  creditPeriodDays: 30,
};

function SettingRow({
  label,
  description,
  checked,
  onChange,
  saving,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  saving: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, py: 1.5 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            color="primary"
            disabled={saving}
          />
        }
        label={
          <Typography variant="caption" sx={{ color: brand.inkSoft, minWidth: 24 }}>
            {checked ? 'Yes' : 'No'}
          </Typography>
        }
        sx={{ mr: 0, ml: 0 }}
      />
    </Box>
  );
}

function RetentionRow({
  label, description, value, onChange, unit, min, max, settingKey, saving, onSave,
}: {
  label: string; description: string; value: string; onChange: (v: string) => void;
  unit: string; min: number; max: number; settingKey: string;
  saving: string | null; onSave: (key: string, value: string) => void;
}) {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mb: 1 }}>{description}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
        <TextField size="small" type="number" value={value}
          onChange={e => onChange(e.target.value)}
          inputProps={{ min, max }}
          sx={{ width: 130 }} helperText={`${unit} (${min}–${max})`} />
        <Button variant="outlined" size="small"
          onClick={() => onSave(settingKey, value)}
          disabled={saving === settingKey}
          startIcon={saving === settingKey ? <CircularProgress size={12} color="inherit" /> : <SaveIcon />}
          sx={{ mb: 2.5 }}>
          {saving === settingKey ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

export function SettingsPage() {
  const { settings: localSettings, update: updateLocal } = useAppSettings();
  const { hasAnyRole } = useAuthStore();
  const isSuperAdmin = hasAnyRole(['SUPER_ADMIN']);

  const [server, setServer] = useState<TenantSettings>(DEFAULT_SERVER_SETTINGS);
  const [loading, setLoading] = useState(!isSuperAdmin);
  const [saving, setSaving] = useState(false);
  const [savedToast, setSavedToast] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscription config — super admin only
  const [subConfig, setSubConfig] = useState<SubscriptionConfig | null>(null);

  // Data retention — super admin only
  const [invoiceBlobMonths, setInvoiceBlobMonths] = useState('12');
  const [orderRetentionYears, setOrderRetentionYears] = useState('6');
  const [notifRetentionMonths, setNotifRetentionMonths] = useState('6');
  const [cartRetentionMonths, setCartRetentionMonths] = useState('6');
  const [peerBlockYears, setPeerBlockYears] = useState('3');
  const [invoiceRecordYears, setInvoiceRecordYears] = useState('7');
  const [deletedProductYears, setDeletedProductYears] = useState('2');
  const [inactiveUserYears, setInactiveUserYears] = useState('3');
  const [refreshTokenDays, setRefreshTokenDays] = useState('30');
  const [retentionSaving, setRetentionSaving] = useState<string | null>(null);
  const [subPct, setSubPct] = useState('');
  const [subCeiling, setSubCeiling] = useState('');
  const [subGstRate, setSubGstRate] = useState('18');
  const [subDiscountPct, setSubDiscountPct] = useState('');
  const [subDiscountMonths, setSubDiscountMonths] = useState('');
  const [subSaving, setSubSaving] = useState(false);

  // Tenant settings search — super admin only
  const [tenantQuery, setTenantQuery] = useState('');
  const [tenantResults, setTenantResults] = useState<AdminTenant[]>([]);
  const [tenantSearching, setTenantSearching] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<AdminTenant | null>(null);
  const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
  const [editingCredit, setEditingCredit] = useState(false);
  const [creditInput, setCreditInput] = useState('');
  const [savingCredit, setSavingCredit] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      getConfig()
        .then(c => {
          setSubConfig(c);
          setSubPct(String(c.revenuePercentage));
          setSubCeiling(c.ceilingAmount != null ? String(c.ceilingAmount) : '');
          setSubGstRate(String(c.gstRate));
          setSubDiscountPct(c.newUserDiscountPct != null ? String(c.newUserDiscountPct) : '');
          setSubDiscountMonths(c.newUserDiscountMonths != null ? String(c.newUserDiscountMonths) : '');
        })
        .catch(() => {});
      adminSettingsApi.getAll()
        .then(settings => {
          const find = (key: string) => settings.find(s => s.key === key)?.value;
          const set = (key: string, setter: (v: string) => void) => { const v = find(key); if (v) setter(v); };
          set('invoice_blob_retention_months',  setInvoiceBlobMonths);
          set('order_retention_years',          setOrderRetentionYears);
          set('notification_retention_months',  setNotifRetentionMonths);
          set('cart_retention_months',          setCartRetentionMonths);
          set('peer_block_retention_years',     setPeerBlockYears);
          set('invoice_record_retention_years', setInvoiceRecordYears);
          set('deleted_product_retention_years',setDeletedProductYears);
          set('inactive_user_retention_years',  setInactiveUserYears);
          set('refresh_token_retention_days',   setRefreshTokenDays);
        })
        .catch(() => {});
      return; // super admin doesn't load tenant settings
    }
    tenantApi.getSettings()
      .then(s => setServer(s))
      .catch(e => setError((e as ApiError).message || 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, [isSuperAdmin]);

  const handleSaveSubConfig = async () => {
    setSubSaving(true);
    try {
      const c = await updateConfig({
        revenuePercentage: parseFloat(subPct),
        ceilingAmount: subCeiling ? parseFloat(subCeiling) : null,
        gstRate: subGstRate ? parseFloat(subGstRate) : null,
        newUserDiscountPct: subDiscountPct ? parseFloat(subDiscountPct) : null,
        newUserDiscountMonths: subDiscountMonths ? parseInt(subDiscountMonths, 10) : null,
      });
      setSubConfig(c);
      setSubGstRate(String(c.gstRate));
      setSubDiscountPct(c.newUserDiscountPct != null ? String(c.newUserDiscountPct) : '');
      setSubDiscountMonths(c.newUserDiscountMonths != null ? String(c.newUserDiscountMonths) : '');
      setSavedToast(true);
    } catch (e) {
      setError((e as ApiError).message || 'Failed to save subscription config');
    } finally { setSubSaving(false); }
  };

  async function saveToggle(patch: Partial<TenantSettings>) {
    const next = { ...server, ...patch };
    setServer(next);
    setSaving(true);
    try {
      const saved = await tenantApi.updateSettings(next);
      setServer(saved);
      setSavedToast(true);
    } catch (e) {
      setError((e as ApiError).message || 'Failed to save settings');
      setServer(server);
    } finally {
      setSaving(false);
    }
  }

  const handleSaveRetention = async (key: string, rawValue: string) => {
    setRetentionSaving(key);
    try {
      await adminSettingsApi.update(key, rawValue);
      setSavedToast(true);
    } catch (e) {
      setError((e as ApiError).message || 'Failed to save setting');
    } finally { setRetentionSaving(null); }
  };

  async function saveOrderSettings() {
    setSaving(true);
    try {
      const saved = await tenantApi.updateSettings(server);
      setServer(saved);
      setSavedToast(true);
    } catch (e) {
      setError((e as ApiError).message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  const handleTenantSearch = async (q: string) => {
    setTenantQuery(q);
    setSelectedTenant(null);
    setTenantSettings(null);
    if (!q.trim()) { setTenantResults([]); return; }
    setTenantSearching(true);
    try {
      const page = await adminApi.listTenants({ q: q.trim(), type: 'WHOLESALER', size: 8 });
      setTenantResults(page.content);
    } catch { setTenantResults([]); }
    finally { setTenantSearching(false); }
  };

  const handleSelectTenant = async (t: AdminTenant) => {
    setSelectedTenant(t);
    setTenantResults([]);
    setTenantQuery(t.name);
    setEditingCredit(false);
    setTenantSettings(null);
    try {
      const s = await adminApi.getTenantSettings(t.id);
      setTenantSettings(s);
      setCreditInput(String(s.creditPeriodDays || 30));
    } catch { /* leave null — shown as — */ }
  };

  const handleSaveCredit = async () => {
    if (!selectedTenant || !tenantSettings) return;
    const days = parseInt(creditInput, 10);
    if (!days || days < 1) return;
    setSavingCredit(true);
    try {
      const updated = await adminApi.updateTenantSettings(selectedTenant.id, { ...tenantSettings, creditPeriodDays: days });
      setTenantSettings(updated);
      setEditingCredit(false);
      setSavedToast(true);
    } catch (e) {
      setError((e as ApiError).message || 'Failed to save');
    } finally { setSavingCredit(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, md: 0 }, py: 3 }}>
      <PageHeader title="Settings" />

      {/* ── Subscription Config — super admin only ───────────────────── */}
      {isSuperAdmin && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
              Subscription Billing Config
            </Typography>
            <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 2 }}>
              Controls how monthly platform fees are calculated for wholesalers.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Revenue Percentage
                </Typography>
                <TextField size="small" value={subPct}
                  onChange={e => setSubPct(e.target.value)}
                  placeholder="e.g. 0.004" sx={{ width: 150 }}
                  helperText="Fraction of total sales" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Ceiling Amount (₹)
                </Typography>
                <TextField size="small" value={subCeiling}
                  onChange={e => setSubCeiling(e.target.value)}
                  placeholder="No limit" sx={{ width: 150 }}
                  helperText="Max fee per invoice" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  GST Rate (%)
                </Typography>
                <TextField size="small" value={subGstRate}
                  onChange={e => setSubGstRate(e.target.value)}
                  placeholder="18" sx={{ width: 110 }}
                  helperText="Applied on fee" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  New User Discount (%)
                </Typography>
                <TextField size="small" value={subDiscountPct}
                  onChange={e => setSubDiscountPct(e.target.value)}
                  placeholder="e.g. 100" sx={{ width: 130 }}
                  helperText="% off fee" />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Upto Months
                </Typography>
                <TextField size="small" value={subDiscountMonths}
                  onChange={e => setSubDiscountMonths(e.target.value)}
                  placeholder="e.g. 2" sx={{ width: 110 }}
                  helperText="Since reg." />
              </Box>
              <Button variant="contained" onClick={handleSaveSubConfig} disabled={subSaving}
                startIcon={subSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                sx={{ mb: 2.5 }}>
                {subSaving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
            {subConfig && (
              <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                Current: {(subConfig.revenuePercentage * 100).toFixed(4)}% fee
                {subConfig.ceilingAmount != null ? `, ceiling ₹${subConfig.ceilingAmount}` : ', no ceiling'}
                {`, GST ${subConfig.gstRate}%`}
                {subConfig.newUserDiscountPct != null && subConfig.newUserDiscountMonths != null
                  ? `, new user ${subConfig.newUserDiscountPct}% off for ${subConfig.newUserDiscountMonths} mo.`
                  : ', no new user discount'}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Data Retention — super admin only ────────────────────────── */}
      {isSuperAdmin && (
        <Card sx={{ mt: 2 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
              Data Retention
            </Typography>
            <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 2.5 }}>
              Scheduled cleanup runs daily. Changes take effect the next day without a server restart.
            </Typography>

            <Stack spacing={3} divider={<Divider />}>
              <RetentionRow
                label="Invoice file storage"
                description="After this period the uploaded PDF/image data is permanently deleted. The invoice record (filename, date) is kept. Default 12."
                value={invoiceBlobMonths} onChange={setInvoiceBlobMonths}
                unit="months" min={1} max={24}
                settingKey="invoice_blob_retention_months"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Order records"
                description="Orders (placed and received) older than this are permanently deleted — line items, shortages and invoice documents included. Default 6 (GST audit window)."
                value={orderRetentionYears} onChange={setOrderRetentionYears}
                unit="years" min={1} max={20}
                settingKey="order_retention_years"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Notifications"
                description="In-app notification records older than this are deleted. Default 6."
                value={notifRetentionMonths} onChange={setNotifRetentionMonths}
                unit="months" min={1} max={60}
                settingKey="notification_retention_months"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Abandoned carts"
                description="Saved carts that have not been updated in this period are deleted. Default 6."
                value={cartRetentionMonths} onChange={setCartRetentionMonths}
                unit="months" min={1} max={24}
                settingKey="cart_retention_months"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Peer block records"
                description="Old peer-block entries are removed after this period. Active blocks created recently are unaffected. Default 3."
                value={peerBlockYears} onChange={setPeerBlockYears}
                unit="years" min={1} max={10}
                settingKey="peer_block_retention_years"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="GST invoice records"
                description="Full GST invoice records (subscription/billing invoices). Minimum 7 years required for GST compliance. Default 7."
                value={invoiceRecordYears} onChange={setInvoiceRecordYears}
                unit="years" min={7} max={20}
                settingKey="invoice_record_retention_years"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Soft-deleted products"
                description="Products marked as deleted are permanently removed after this period. Safe once older than the order retention window. Default 2."
                value={deletedProductYears} onChange={setDeletedProductYears}
                unit="years" min={1} max={10}
                settingKey="deleted_product_retention_years"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Inactive user accounts"
                description="Users with no login for this period are anonymised (name/email/phone/password wiped, account disabled). Required by DPDP Act. Default 3."
                value={inactiveUserYears} onChange={setInactiveUserYears}
                unit="years" min={1} max={10}
                settingKey="inactive_user_retention_years"
                saving={retentionSaving} onSave={handleSaveRetention} />

              <RetentionRow
                label="Refresh token lifetime"
                description="JWT refresh tokens are stateless — this setting documents the intended lifetime and is applied in future token-store implementations. Default 30."
                value={refreshTokenDays} onChange={setRefreshTokenDays}
                unit="days" min={7} max={90}
                settingKey="refresh_token_retention_days"
                saving={retentionSaving} onSave={handleSaveRetention} />
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Tenant Settings — super admin only ──────────────────────── */}
      {isSuperAdmin && (
        <Card sx={{ mt: 2, overflow: 'visible' }}>
          <CardContent sx={{ p: { xs: 2, md: 3 }, overflow: 'visible' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
              Tenant Settings
            </Typography>
            <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 2 }}>
              Search a wholesaler tenant to view and update their per-tenant settings such as credit period.
            </Typography>

            <Box sx={{ position: 'relative', maxWidth: 400 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search wholesaler by name or code…"
                value={tenantQuery}
                onChange={e => handleTenantSearch(e.target.value)}
                InputProps={{
                  endAdornment: tenantSearching
                    ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                    : null,
                }}
              />
              {tenantResults.length > 0 && (
                <List dense sx={{
                  position: 'absolute', zIndex: 1300, width: '100%', bgcolor: 'background.paper',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  boxShadow: 3, mt: 0.5, maxHeight: 240, overflow: 'auto',
                }}>
                  {tenantResults.map(t => (
                    <ListItemButton key={t.id} onClick={() => handleSelectTenant(t)}>
                      <ListItemText
                        primary={t.name}
                        secondary={t.code}
                        primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: 11 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            {selectedTenant && (
              <Box sx={{ mt: 2.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{selectedTenant.name}</Typography>
                  <Chip label={selectedTenant.code} size="small" sx={{ fontSize: 11 }} />
                </Box>

                <Divider sx={{ mb: 1.5 }} />

                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Credit Period</Typography>
                <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mb: 1 }}>
                  Days allowed before an unpaid invoice triggers AT_RISK / auto-block status.
                </Typography>

                {!editingCredit ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
                      {tenantSettings ? tenantSettings.creditPeriodDays || 30 : '—'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: brand.inkSoft }}>days</Typography>
                    {tenantSettings && (
                      <Button size="small" variant="outlined" onClick={() => setEditingCredit(true)}>
                        Edit
                      </Button>
                    )}
                    {!tenantSettings && <CircularProgress size={14} />}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={creditInput}
                      onChange={e => setCreditInput(e.target.value)}
                      inputProps={{ min: 1, max: 365 }}
                      sx={{ width: 110 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                    />
                    <Button size="small" variant="contained" onClick={handleSaveCredit} disabled={savingCredit}
                      startIcon={savingCredit ? <CircularProgress size={12} color="inherit" /> : <SaveIcon />}>
                      {savingCredit ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="small" onClick={() => { setEditingCredit(false); setCreditInput(String(tenantSettings?.creditPeriodDays || 30)); }}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Wholesaler / tenant settings — hidden for super admin ────── */}
      {!isSuperAdmin && (
        <>
      {/* ── Price Visibility ─────────────────────────────────────────── */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
            Price Visibility
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 1.5 }}>
            Control who can see product prices in your catalogue.
          </Typography>

          <SettingRow
            label="Show price to parties"
            description="When on, parties browsing your catalogue will see product prices."
            checked={server.showPriceToParties}
            onChange={v => saveToggle({ showPriceToParties: v })}
            saving={saving}
          />
          <Divider />
          <SettingRow
            label="Show price to sales reps"
            description="When on, your linked sales representatives will see product prices."
            checked={server.showPriceToSalesReps}
            onChange={v => saveToggle({ showPriceToSalesReps: v })}
            saving={saving}
          />
        </CardContent>
      </Card>

      {/* ── Stock Visibility ─────────────────────────────────────────── */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
            Stock Visibility
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 1.5 }}>
            Control who can see your current stock levels.
          </Typography>

          <SettingRow
            label="Show stock to parties"
            description="When on, parties browsing your catalogue will see available stock quantities."
            checked={server.showStockToParties}
            onChange={v => saveToggle({ showStockToParties: v })}
            saving={saving}
          />
          <Divider />
          <SettingRow
            label="Show stock to sales reps"
            description="When on, your linked sales representatives will see available stock quantities."
            checked={server.showStockToSalesReps}
            onChange={v => saveToggle({ showStockToSalesReps: v })}
            saving={saving}
          />
        </CardContent>
      </Card>

      {/* ── Scheme Visibility ────────────────────────────────────────── */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
            Scheme Visibility
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 1.5 }}>
            Control whether active product schemes are visible to others.
          </Typography>

          <SettingRow
            label="Show schemes to parties"
            description="When on, parties can see any active schemes attached to your products."
            checked={server.showSchemesToParties}
            onChange={v => saveToggle({ showSchemesToParties: v })}
            saving={saving}
          />
          <Divider />
          <SettingRow
            label="Show schemes to sales reps"
            description="When on, sales representatives can see active schemes on your products."
            checked={server.showSchemesToSalesReps}
            onChange={v => saveToggle({ showSchemesToSalesReps: v })}
            saving={saving}
          />
        </CardContent>
      </Card>

      {/* ── Product Visibility ───────────────────────────────────────── */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
            Product Visibility
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 1.5 }}>
            Control product listing behaviour for specific audiences.
          </Typography>

          <SettingRow
            label="Show held products to sales reps"
            description="When on, products placed on hold are still visible to sales representatives."
            checked={server.showHeldProductsToSalesReps}
            onChange={v => saveToggle({ showHeldProductsToSalesReps: v })}
            saving={saving}
          />
        </CardContent>
      </Card>

      {/* ── Order Settings ───────────────────────────────────────────── */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
            Order Settings
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 2.5 }}>
            Set order constraints and availability windows.
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Minimum order amount
              </Typography>
              <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mb: 1 }}>
                Parties must reach this total before placing an order. Set to 0 to allow any order value.
              </Typography>
              <TextField
                size="small"
                type="number"
                value={server.minOrderAmount}
                onChange={e => {
                  const v = parseFloat(e.target.value);
                  setServer(prev => ({ ...prev, minOrderAmount: isNaN(v) || v < 0 ? 0 : v }));
                }}
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="caption" sx={{ color: brand.inkSoft }}>₹</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Order pause window
              </Typography>
              <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mb: 1 }}>
                During this window parties will see a message that you are not accepting orders. Orders can still be placed.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                <TextField
                  size="small"
                  type="datetime-local"
                  label="From"
                  value={server.orderPauseFrom ? server.orderPauseFrom.slice(0, 16) : ''}
                  onChange={e => setServer(prev => ({ ...prev, orderPauseFrom: e.target.value ? e.target.value + ':00' : null }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 210 }}
                />
                <Typography variant="body2" sx={{ color: brand.inkSoft }}>to</Typography>
                <TextField
                  size="small"
                  type="datetime-local"
                  label="To"
                  value={server.orderPauseTo ? server.orderPauseTo.slice(0, 16) : ''}
                  onChange={e => setServer(prev => ({ ...prev, orderPauseTo: e.target.value ? e.target.value + ':00' : null }))}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 210 }}
                />
              </Stack>
            </Box>
          </Stack>

          <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={saving}
              onClick={saveOrderSettings}
              sx={{ minWidth: 140 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </CardContent>
      </Card>

        </>
      )} {/* end !isSuperAdmin */}

      {/* ── Local Preferences — all users ───────────────────────────── */}
      <Card sx={{ mt: 2 }}>
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: brand.navy, mb: 0.5 }}>
            Local Preferences
          </Typography>
          <Typography variant="body2" sx={{ color: brand.inkSoft, mb: 2 }}>
            Saved on this device only. Changes apply immediately.
          </Typography>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Low stock threshold
            </Typography>
            <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block', mb: 1 }}>
              Products at or below this quantity are highlighted as low stock in your product list.
            </Typography>
            <TextField
              size="small"
              type="number"
              value={localSettings.lowStockThreshold}
              onChange={e => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v) && v >= 0) updateLocal({ lowStockThreshold: v });
              }}
              inputProps={{ min: 0, max: 9999 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="caption" sx={{ color: brand.inkSoft }}>units</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ width: 160 }}
            />
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={savedToast}
        autoHideDuration={3000}
        onClose={() => setSavedToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSavedToast(false)}>
          Settings saved.
        </Alert>
      </Snackbar>

      <Snackbar
        open={error !== null}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
